import React, { useCallback, useContext, useRef, useState } from 'react';
import classnames from 'clsx';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import style from './style.module.css';
import Global from '../../../../../../../../src/store/global-selectors';
import { useEnableLotTracking } from '../../../../../../../feature-flags/hooks/use-enable-lot-tracking';
import { useLuxonUserDateFormat } from '../../../../../../../hooks/use-datetime-formats';
import { getDateAsUtcNoonInISO } from '../../../../../../../services/datetime-service';
import { addInventoryForProduct } from '../../../../../../../store/inventory/inventory.api';
import { ProductDetailsContext } from '../../../../../context';
import { CostInput } from '../../shared-components/cost-input';
import { ExpirationDateInput } from '../../shared-components/expiration-date-input';
import { InventoryLocationByWarehousePicker } from '../../shared-components/location-by-warehouse-picker';
import { LotInput } from '../../shared-components/lot-input';
import { NameAndSku as NameAndSkuFields } from '../../shared-components/name-and-sku';
import { NotesInput } from '../../shared-components/notes-input';
import { QuantityInput } from '../../shared-components/quantity-input';
import sharedStyle from '../../shared-components/style.module.css';
import { WarehousePicker } from '../../shared-components/warehouse-picker';

/**
 * @typedef {import('../../prop-types/inventory-warehouse').InventoryWarehouse} InventoryWarehouse
 * @typedef {import('../../prop-types/warehouse-location').WarehouseLocation} WarehouseLocation
 */

/**
 * Gets the default warehouse ID based on warehouse configuration
 * @param {InventoryWarehouse[]} warehouses
 * @returns {number | undefined}
 */
const getDefaultWarehouseId = (warehouses) => {
  if (warehouses.length === 1) {
    return warehouses[0].inventoryWarehouseId;
  }
  const defaultWarehouse = warehouses.find((warehouse) => warehouse.linkToDefault);
  return defaultWarehouse?.inventoryWarehouseId;
};

/**
 * Finds a location by name with case-insensitive and whitespace-tolerant matching
 * @param {WarehouseLocation[]} locations
 * @param {string} locationName
 * @returns {WarehouseLocation | undefined}
 */
const findLocationByName = (locations, locationName) => {
  const normalizedSearchName = locationName?.trim().toLowerCase();
  if (!normalizedSearchName) {
    return undefined;
  }
  return locations.find((loc) => loc.name?.trim().toLowerCase() === normalizedSearchName);
};

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {InventoryWarehouse[]} props.inventoryWarehouses
 * @param {WarehouseLocation[]} props.inventoryWarehouseLocations
 * @param {number} props.productId
 * @param {string} props.productName
 * @param {string} props.productSku
 * @param {() => void} props.onReloadLocations
 * @param {string} [props.productWarehouseLocation]
 * @returns {JSX.Element}
 */

export const AddStockModal = ({
  onClose,
  inventoryWarehouses,
  inventoryWarehouseLocations,
  productId,
  productName,
  productSku,
  onReloadLocations,
  productWarehouseLocation,
}) => {
  const { imageUrl: thumbnailUrl } = useContext(ProductDetailsContext);
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const isLotTrackingEnabled = useEnableLotTracking();
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(() => getDefaultWarehouseId(inventoryWarehouses));
  const locationsForWarehouse = inventoryWarehouseLocations.filter(
    (loc) => loc.inventoryWarehouseId === selectedWarehouseId && loc.isActive,
  );
  const [selectedLocationId, setSelectedLocationId] = useState(() => {
    if (!productWarehouseLocation) {
      return undefined;
    }
    const warehouseId = getDefaultWarehouseId(inventoryWarehouses);
    const filteredLocations = inventoryWarehouseLocations.filter((loc) => loc.inventoryWarehouseId === warehouseId && loc.isActive);

    const matchingLocation = findLocationByName(filteredLocations, productWarehouseLocation);
    return matchingLocation?.inventoryLocationId;
  });
  const [quantity, setQuantity] = useState(0);
  const [cost, setCost] = useState();
  const [expirationDate, setExpirationDate] = useState('');
  const [lot, setLot] = useState('');
  const [notes, setNotes] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const userDateFormat = useLuxonUserDateFormat();

  const onAddInventory = useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const expirationDateISO = getDateAsUtcNoonInISO(expirationDate, userDateFormat);
    const addProps = {
      productId,
      inventoryLocationId: selectedLocationId,
      quantity,
      cost: {
        value: cost,
        code: sellerCurrencyCode,
      },
      description: notes,
      lot,
      expirationDate: expirationDateISO,
    };

    const addInventoryPromise = addInventoryForProduct(addProps);
    addInventoryPromise.promise
      .then(() => {
        onReloadLocations();
        onClose();
      })
      .catch(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.addStockErrorTitle} />,
          content: <FormattedMessage {...messages.addStockErrorMessage} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .finally(() => {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      });
  }, [
    productId,
    selectedLocationId,
    quantity,
    cost,
    sellerCurrencyCode,
    notes,
    lot,
    expirationDate,
    onClose,
    onReloadLocations,
    userDateFormat,
  ]);

  const hasEmptyLotID = expirationDate && lot === '';
  const shouldDisableSaveForLot = isLotTrackingEnabled && hasEmptyLotID;
  const shouldDisableSave = quantity === 0 || !(selectedWarehouseId && selectedLocationId) || isSubmitting;

  const selectedWarehouse = inventoryWarehouses.find((wrh) => wrh.inventoryWarehouseId === selectedWarehouseId);
  return (
    <Modal className={style['alternate-locations-modal']}>
      <ModalHeader closeModal={onClose}>
        <FormattedMessage {...messages.addStockToLocationModalTitle} />
      </ModalHeader>
      <ModalBody>
        <div className={style['alternate-location-wrapper']}>
          <div className={style['field-section']}>
            <NameAndSkuFields productName={productName} productSku={productSku} thumbnailUrl={thumbnailUrl} />
          </div>
          <div
            className={classnames(sharedStyle['dual-field'], sharedStyle.balanced, style['field-section'], style['field-group-wrapper'])}
          >
            <WarehousePicker
              inventoryWarehouses={inventoryWarehouses}
              selectedWarehouseId={selectedWarehouseId}
              setSelectedWarehouseId={setSelectedWarehouseId}
            />
            <InventoryLocationByWarehousePicker
              allowableLocations={locationsForWarehouse}
              isDisabled={!selectedWarehouse}
              selectedLocationId={selectedLocationId}
              setSelectedLocationId={setSelectedLocationId}
            />
          </div>
          <div className={style['field-group-wrapper']}>
            <div className={classnames(sharedStyle['dual-field'], sharedStyle.balanced)}>
              <QuantityInput onChange={setQuantity} quantity={quantity} slimInput={false} />
              <CostInput cost={cost} setCost={setCost} slimInput={false} />
            </div>
            {isLotTrackingEnabled ? (
              <div className={classnames(sharedStyle['dual-field'], sharedStyle.balanced)}>
                <LotInput lot={lot} setLot={setLot} />
                <ExpirationDateInput expirationDate={expirationDate} setExpirationDate={setExpirationDate} />
              </div>
            ) : null}
            <NotesInput notes={notes} setNotes={setNotes} />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className={style['modal-buttons']}>
          <Button onClick={onClose} theme={THEME_CONSTANTS.tertiary}>
            <FormattedMessage {...messages.alternateLocationModalCancel} />
          </Button>
          <Button disabled={shouldDisableSave || shouldDisableSaveForLot} onClick={onAddInventory} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.alternateLocationModalAddLocation} />
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
